import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  CookieCutterParams,
  CookieShape,
  generateCutterGeometry,
  generateStampGeometry,
  exportCookieCutterToSTL
} from '@/lib/cookieCutterGenerator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Download,
  RotateCcw,
  Box,
  Layers,
  Heart,
  Star,
  Circle,
  Flower2,
  Cloud,
  Hexagon,
  Sliders,
  Sparkles,
  Scissors,
  RotateCw,
  Crosshair
} from 'lucide-react';
import { toast } from 'sonner';

const defaultParams: CookieCutterParams = {
  shape: 'heart',
  size: 75,
  bladeHeight: 15,
  bladeThickness: 1.0,
  flangeWidth: 4.5,
  flangeHeight: 2.5,
  taperCuttingEdge: true,
  hasStamp: true,
  stampThickness: 2.5,
  stampReliefHeight: 1.8,
  stampHandleHeight: 14,
  stampHandleDiameter: 18,
  stampClearance: 1.2
};

export default function CookieCutterPage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const [params, setParams] = useState<CookieCutterParams>(defaultParams);
  const [wireframe, setWireframe] = useState(false);
  const [cutterColor, setCutterColor] = useState('#B85D3B'); // Terracota
  const [stampColor, setStampColor] = useState('#D8C7B5');   // Lino/Arena
  const [activeTab, setActiveTab] = useState<'cutter' | 'stamp'>('cutter');
  const [autoRotate, setAutoRotate] = useState(false);

  // Three.js refs
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cutterMeshRef = useRef<THREE.Mesh | null>(null);
  const stampMeshRef = useRef<THREE.Mesh | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const cutterGeomRef = useRef<THREE.BufferGeometry | null>(null);
  const stampGeomRef = useRef<THREE.BufferGeometry | null>(null);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
    }
  }, [autoRotate]);

  // Inicializar Three.js
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const width = container.clientWidth;
    const height = container.clientHeight || 560;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xF9F8F6);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
    camera.position.set(0, 110, 160);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    rendererRef.current = renderer;

    container.appendChild(renderer.domElement);

    // Iluminación
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.85);
    dirLight1.position.set(120, 180, 120);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xE59866, 0.4);
    dirLight2.position.set(-120, 90, -90);
    scene.add(dirLight2);

    // Grilla Japandi
    const grid = new THREE.GridHelper(240, 24, 0xD4CDC5, 0xEAE4DD);
    grid.position.y = -0.5;
    scene.add(grid);

    // Geometría y Mesh Cortante
    const cutterGeom = generateCutterGeometry(params);
    cutterGeomRef.current = cutterGeom;

    const cutterMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(cutterColor),
      roughness: 0.35,
      metalness: 0.05,
      side: THREE.DoubleSide,
      wireframe
    });
    const cutterMesh = new THREE.Mesh(cutterGeom, cutterMat);
    cutterMeshRef.current = cutterMesh;
    scene.add(cutterMesh);

    // Geometría y Mesh Sello
    if (params.hasStamp) {
      const stampGeom = generateStampGeometry(params);
      stampGeomRef.current = stampGeom;

      const stampMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(stampColor),
        roughness: 0.4,
        metalness: 0.05,
        side: THREE.DoubleSide,
        wireframe
      });
      const stampMesh = new THREE.Mesh(stampGeom, stampMat);
      stampMeshRef.current = stampMesh;
      scene.add(stampMesh);
    }

    // Controles orbitales de cámara
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, params.bladeHeight * 0.4, 0);
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 2.0;
    controls.maxDistance = 450;
    controls.minDistance = 40;
    controlsRef.current = controls;

    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container || !rendererRef.current) return;
      const nw = container.clientWidth;
      const nh = container.clientHeight || 560;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(nw, nh);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      renderer.dispose();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, []);

  const handleRecenter = () => {
    if (!cameraRef.current || !controlsRef.current) return;
    controlsRef.current.target.set(0, params.bladeHeight * 0.4, 0);
    cameraRef.current.position.set(0, 110, 160);
    controlsRef.current.update();
    toast.info('Vista centrada');
  };

  // Actualizar geometrías cuando cambian los parámetros
  useEffect(() => {
    if (!sceneRef.current || !cutterMeshRef.current) return;

    // Actualizar cortante
    const newCutterGeom = generateCutterGeometry(params);
    cutterGeomRef.current = newCutterGeom;
    cutterMeshRef.current.geometry.dispose();
    cutterMeshRef.current.geometry = newCutterGeom;

    // Actualizar sello
    if (params.hasStamp) {
      const newStampGeom = generateStampGeometry(params);
      stampGeomRef.current = newStampGeom;

      if (!stampMeshRef.current) {
        const stampMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(stampColor),
          roughness: 0.4,
          metalness: 0.05,
          side: THREE.DoubleSide,
          wireframe
        });
        const stampMesh = new THREE.Mesh(newStampGeom, stampMat);
        stampMeshRef.current = stampMesh;
        sceneRef.current.add(stampMesh);
      } else {
        stampMeshRef.current.geometry.dispose();
        stampMeshRef.current.geometry = newStampGeom;
        stampMeshRef.current.visible = true;
      }
    } else {
      if (stampMeshRef.current) {
        stampMeshRef.current.visible = false;
      }
    }
  }, [params]);

  // Actualizar materiales
  useEffect(() => {
    if (cutterMeshRef.current) {
      const mat = cutterMeshRef.current.material as THREE.MeshStandardMaterial;
      mat.color.set(cutterColor);
      mat.wireframe = wireframe;
    }
    if (stampMeshRef.current) {
      const mat = stampMeshRef.current.material as THREE.MeshStandardMaterial;
      mat.color.set(stampColor);
      mat.wireframe = wireframe;
    }
  }, [cutterColor, stampColor, wireframe]);

  const handleExportCutter = () => {
    if (!cutterGeomRef.current) return;
    toast.success('Descargando cortante perimetral...');
    exportCookieCutterToSTL(cutterGeomRef.current, `cortante-${params.shape}-${params.size}mm.stl`);
  };

  const handleExportStamp = () => {
    if (!stampGeomRef.current) return;
    toast.success('Descargando sello interior con manija...');
    exportCookieCutterToSTL(stampGeomRef.current, `sello-${params.shape}-${params.size}mm.stl`);
  };

  const shapesList = [
    { id: 'heart', label: 'Corazón', icon: Heart },
    { id: 'star', label: 'Estrella', icon: Star },
    { id: 'circle', label: 'Círculo', icon: Circle },
    { id: 'flower', label: 'Flor Japandi', icon: Flower2 },
    { id: 'cloud', label: 'Nube', icon: Cloud },
    { id: 'hexagon', label: 'Hexágono', icon: Hexagon }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to="/tools"
          className="inline-flex items-center text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Volver al Hub de Herramientas
        </Link>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Generador de Cortantes & Sellos</h1>
              <Badge className="bg-primary/10 text-primary border-primary/20">Cocina & Deco</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Modelá cortantes de galletitas con filo cónico afilado, pestaña de apoyo ergonómica y sello interior con manija.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setParams(defaultParams)}
              className="gap-1.5"
            >
              <RotateCcw className="h-4 w-4" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={handleExportCutter}
              className="gap-1.5 bg-primary text-primary-foreground shadow-sm"
            >
              <Download className="h-4 w-4" /> Cortante STL
            </Button>
            {params.hasStamp && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportStamp}
                className="gap-1.5 shadow-sm"
              >
                <Download className="h-4 w-4" /> Sello STL
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Selector de Siluetas */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Silueta Base
        </Label>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2.5">
          {shapesList.map(s => {
            const isSelected = params.shape === s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setParams({ ...params, shape: s.id as CookieShape })}
                className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1.5 ${
                  isSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                    : 'border-border/70 bg-card hover:bg-muted/40'
                }`}
              >
                <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="text-xs font-semibold">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Grid: 3D Viewport + Controls */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Viewport 3D */}
        <div className="lg:col-span-7 flex flex-col gap-3">
          <Card className="overflow-hidden border border-border/80 shadow-sm relative flex flex-col min-h-[520px] lg:min-h-[620px] bg-[#F9F8F6]">
            {/* Controles flotantes */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between pointer-events-none">
              <div className="pointer-events-auto flex gap-2">
                <Button
                  size="sm"
                  variant={autoRotate ? "default" : "secondary"}
                  className={`text-xs gap-1.5 shadow-sm h-8 ${
                    autoRotate ? 'bg-primary text-primary-foreground' : ''
                  }`}
                  onClick={() => setAutoRotate(!autoRotate)}
                  title="Giro automático 360°"
                >
                  <RotateCw className={`h-3.5 w-3.5 ${autoRotate ? 'animate-spin' : ''}`} />
                  {autoRotate ? 'Giro ON' : 'Giro Auto'}
                </Button>

                <Button
                  size="sm"
                  variant="secondary"
                  className="text-xs gap-1.5 shadow-sm h-8"
                  onClick={handleRecenter}
                  title="Centrar y reajustar vista"
                >
                  <Crosshair className="h-3.5 w-3.5" /> Centrar
                </Button>

                <Button
                  size="sm"
                  variant={wireframe ? "default" : "secondary"}
                  className="text-xs gap-1 shadow-sm h-8"
                  onClick={() => setWireframe(!wireframe)}
                >
                  <Layers className="h-3.5 w-3.5" /> Malla
                </Button>
              </div>

              <div className="pointer-events-auto flex items-center gap-1.5 bg-card/90 backdrop-blur-xs p-1 rounded-lg border border-border/60 shadow-xs">
                {[
                  { cutter: '#B85D3B', stamp: '#D8C7B5', label: 'Terracota / Lino' },
                  { cutter: '#7A8B7B', stamp: '#F5F2EB', label: 'Salvia / Blanco' },
                  { cutter: '#1C1C1E', stamp: '#B85D3B', label: 'Carbón / Terracota' }
                ].map((palette, idx) => (
                  <button
                    key={idx}
                    title={palette.label}
                    className="w-5 h-5 rounded-full border border-black/10 overflow-hidden flex"
                    onClick={() => {
                      setCutterColor(palette.cutter);
                      setStampColor(palette.stamp);
                    }}
                  >
                    <span className="w-1/2 h-full" style={{ backgroundColor: palette.cutter }} />
                    <span className="w-1/2 h-full" style={{ backgroundColor: palette.stamp }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Canvas Three.js */}
            <div ref={mountRef} className="w-full flex-1 cursor-grab active:cursor-grabbing" />

            {/* Pie informativo */}
            <div className="p-3 bg-card/80 backdrop-blur-xs border-t border-border/60 text-xs text-muted-foreground flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Box className="h-3.5 w-3.5 text-primary" />
                Rotar: Arrastre • Zoom: Rueda
              </span>
              <div className="flex items-center gap-3">
                <span>Tamaño: <strong className="text-foreground">{params.size} mm</strong></span>
                <span>Cuchilla: <strong className="text-foreground">{params.bladeHeight} mm</strong></span>
                <span>Sello: <strong className="text-foreground">{params.hasStamp ? 'Incluido' : 'Solo Cortante'}</strong></span>
              </div>
            </div>
          </Card>
        </div>

        {/* Panel Paramétrico */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="border border-border/80 shadow-xs">
            <CardHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sliders className="h-4 w-4 text-primary" /> Parámetros de Fabricación
                </CardTitle>
                <Badge variant="secondary" className="text-[10px] font-mono">
                  {params.shape} • {params.size}mm
                </Badge>
              </div>

              {/* Segment Tabs */}
              <div className="grid grid-cols-2 gap-1.5 pt-3">
                <button
                  onClick={() => setActiveTab('cutter')}
                  className={`py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'cutter'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Scissors className="h-3.5 w-3.5" /> Cortante Exterior
                </button>
                <button
                  onClick={() => setActiveTab('stamp')}
                  className={`py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'stamp'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" /> Sello & Manija
                </button>
              </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* TAB 1: CORTANTE EXTERIOR */}
              {activeTab === 'cutter' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <Label>Tamaño Exterior</Label>
                      <span className="text-primary font-semibold">{params.size} mm</span>
                    </div>
                    <input
                      type="range"
                      min="40"
                      max="140"
                      value={params.size}
                      onChange={e => setParams({ ...params, size: Number(e.target.value) })}
                      className="w-full accent-primary cursor-pointer"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Altura de Cuchilla</Label>
                        <span className="font-semibold">{params.bladeHeight} mm</span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="25"
                        value={params.bladeHeight}
                        onChange={e => setParams({ ...params, bladeHeight: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Grosor de Pared</Label>
                        <span className="font-semibold">{params.bladeThickness} mm</span>
                      </div>
                      <input
                        type="range"
                        min="0.8"
                        max="2.0"
                        step="0.1"
                        value={params.bladeThickness}
                        onChange={e => setParams({ ...params, bladeThickness: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Ancho Pestaña Agarre</Label>
                        <span className="font-semibold">{params.flangeWidth} mm</span>
                      </div>
                      <input
                        type="range"
                        min="2.5"
                        max="8.0"
                        step="0.5"
                        value={params.flangeWidth}
                        onChange={e => setParams({ ...params, flangeWidth: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Altura Pestaña</Label>
                        <span className="font-semibold">{params.flangeHeight} mm</span>
                      </div>
                      <input
                        type="range"
                        min="1.5"
                        max="4.5"
                        step="0.5"
                        value={params.flangeHeight}
                        onChange={e => setParams({ ...params, flangeHeight: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-muted/40 rounded-xl space-y-1.5 border border-border/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-semibold">Filo Cónico (Afilado)</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Reduce el grosor en la punta para un corte perfecto de masa
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        className="rounded h-4 w-4 cursor-pointer accent-primary"
                        checked={params.taperCuttingEdge}
                        onChange={e => setParams({ ...params, taperCuttingEdge: e.target.checked })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: SELLO INTERIOR & MANIJA */}
              {activeTab === 'stamp' && (
                <div className="space-y-4">
                  <div className="p-3 bg-muted/40 rounded-xl space-y-2 border border-border/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-semibold">Generar Sello Interior</Label>
                        <p className="text-[11px] text-muted-foreground">
                          Pieza desmontable con relieve y manija para marcar la masa
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        className="rounded h-4 w-4 cursor-pointer accent-primary"
                        checked={params.hasStamp}
                        onChange={e => setParams({ ...params, hasStamp: e.target.checked })}
                      />
                    </div>
                  </div>

                  {params.hasStamp && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <Label>Base del Sello</Label>
                            <span className="font-semibold">{params.stampThickness} mm</span>
                          </div>
                          <input
                            type="range"
                            min="1.5"
                            max="4.0"
                            step="0.5"
                            value={params.stampThickness}
                            onChange={e => setParams({ ...params, stampThickness: Number(e.target.value) })}
                            className="w-full accent-primary cursor-pointer"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <Label>Profundidad Relieve</Label>
                            <span className="font-semibold text-primary">{params.stampReliefHeight} mm</span>
                          </div>
                          <input
                            type="range"
                            min="1.0"
                            max="3.5"
                            step="0.2"
                            value={params.stampReliefHeight}
                            onChange={e => setParams({ ...params, stampReliefHeight: Number(e.target.value) })}
                            className="w-full accent-primary cursor-pointer"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <Label>Altura Manija</Label>
                            <span className="font-semibold">{params.stampHandleHeight} mm</span>
                          </div>
                          <input
                            type="range"
                            min="8"
                            max="25"
                            value={params.stampHandleHeight}
                            onChange={e => setParams({ ...params, stampHandleHeight: Number(e.target.value) })}
                            className="w-full accent-primary cursor-pointer"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <Label>Diámetro Manija</Label>
                            <span className="font-semibold">{params.stampHandleDiameter} mm</span>
                          </div>
                          <input
                            type="range"
                            min="12"
                            max="26"
                            value={params.stampHandleDiameter}
                            onChange={e => setParams({ ...params, stampHandleDiameter: Number(e.target.value) })}
                            className="w-full accent-primary cursor-pointer"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <Label>Holgura / Tolerancia de Encastre</Label>
                          <span className="font-semibold">{params.stampClearance} mm</span>
                        </div>
                        <input
                          type="range"
                          min="0.6"
                          max="2.5"
                          step="0.1"
                          value={params.stampClearance}
                          onChange={e => setParams({ ...params, stampClearance: Number(e.target.value) })}
                          className="w-full accent-primary cursor-pointer"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Separación perimetral para que el sello deslice sin atascarse en el cortante.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
